/**
 * SubscriptionWatchdog
 *
 * Two-in-one component:
 *
 *  1. WatchBadge — compact "clock" indicator rendered inside the FrontDeskLayout
 *     topbar. Shows days remaining with a color-coded urgency ring. Hovering
 *     reveals a tooltip with the exact period-end date and plan name.
 *
 *  2. ExpiredOverlay — full-screen lockout that replaces the entire UI when the
 *     subscription has expired. It:
 *       - Blocks all UI interaction (pointer-events: none on the main layout)
 *       - Stops the frontend from issuing new API requests (via a module-level flag)
 *       - Calls onLogout() to invalidate all active sessions in this browser tab
 *       - Shows the tenantAdmin a clear renewal button pointing to Main Hall
 *
 * Props:
 *   subscription  — return value of useSubscription()
 *   onLogout      — FrontDeskLayout's logout handler (clears auth + redirects)
 *   tenantSlug    — used for the renewal URL
 *   mainHallUrl   — base URL of Main Hall (default: window.origin port 3000)
 *
 * Usage in FrontDeskLayout:
 *   import { SubscriptionWatchdog, WatchBadge } from '../components/SubscriptionWatchdog.jsx';
 *
 *   // In the topbar, after the clock:
 *   <WatchBadge subscription={subscription} />
 *
 *   // At the root of the layout (wrapping the main content):
 *   <SubscriptionWatchdog subscription={subscription} onLogout={onLogout} tenantSlug={tenantSlug} />
 */

import React, { useEffect, useRef, useState } from 'react';
import { T, FONTS } from '../constants/theme.js';
import { clearAuth, getTenantSlug } from '../services/api.js';

// ── Internal: block API requests when expired ────────────────────────────────
// A simple module-level flag checked by apiFetch. Set to true the moment the
// watchdog detects expiry so no further requests reach the backend.
let _subscriptionBlocked = false;
export function isSubscriptionBlocked()    { return _subscriptionBlocked; }
export function setSubscriptionBlocked(v)  { _subscriptionBlocked = v; }

// ── Urgency → colour map ─────────────────────────────────────────────────────
const URGENCY_COLORS = {
  ok:       { ring: '#22c55e', text: '#16a34a', bg: '#f0fdf4', glow: '0 0 6px #22c55e88' },
  warning:  { ring: '#f59e0b', text: '#d97706', bg: '#fffbeb', glow: '0 0 6px #f59e0b88' },
  critical: { ring: '#ef4444', text: '#dc2626', bg: '#fef2f2', glow: '0 0 8px #ef444488' },
  expired:  { ring: '#6b7280', text: '#4b5563', bg: '#f9fafb', glow: 'none'               },
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric',
    });
  } catch { return iso; }
}

function formatPlan(pricingModel) {
  if (!pricingModel) return '';
  return { monthly: 'Monthly Plan', annual: 'Annual Plan', lifetime: 'Lifetime' }[pricingModel]
      ?? pricingModel.charAt(0).toUpperCase() + pricingModel.slice(1);
}

/** Zero-pad a number to 2 digits for HH:MM:SS display */
function pad2(n) {
  return String(n).padStart(2, '0');
}

// ── WatchBadge — compact topbar indicator ────────────────────────────────────

/**
 * Renders a large, prominent "live watch" badge in the topbar.
 * Should be placed after the clock div in FrontDeskLayout.
 *
 * Shows a continuously-ticking countdown (months · days · HH:MM:SS) with a
 * color-coded urgency state. Hovering reveals a tooltip with the exact
 * period-end date and plan name.
 */
export function WatchBadge({ subscription }) {
  const [showTip, setShowTip] = useState(false);
  const tipRef = useRef(null);

  // Loading state — show a neutral placeholder
  if (!subscription || subscription.loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#334155', opacity: 0.4 }} />
      </div>
    );
  }

  const { urgency, isLifetime, isExpired, monthsRemaining, daysRemaining,
          hoursRemaining, minutesRemaining, secondsRemaining,
          periodEnd, pricingModel, status } = subscription;

  const col = URGENCY_COLORS[urgency] ?? URGENCY_COLORS.ok;

  // ── Build the live countdown string (like a normal watch) ────────────────
  let countdown;
  if (isLifetime)      countdown = 'LIFETIME';
  else if (isExpired)  countdown = 'EXPIRED';
  else {
    const hh = pad2(hoursRemaining);
    const mm = pad2(minutesRemaining);
    const ss = pad2(secondsRemaining);
    if (monthsRemaining > 0) {
      countdown = `${monthsRemaining}mo ${daysRemaining}d ${hh}:${mm}:${ss}`;
    } else if (daysRemaining > 0) {
      countdown = `${daysRemaining}d ${hh}:${mm}:${ss}`;
    } else {
      countdown = `${hh}:${mm}:${ss}`;
    }
  }

  const tooltipText = isLifetime
    ? 'Lifetime subscription — no expiry'
    : isExpired
      ? `Subscription expired on ${formatDate(periodEnd)}`
      : `${formatPlan(pricingModel)} · expires ${formatDate(periodEnd)}`;

  return (
    <div
      ref={tipRef}
      onMouseEnter={() => setShowTip(true)}
      onMouseLeave={() => setShowTip(false)}
      style={{ position: 'relative', flexShrink: 0 }}
    >
      {/* Badge pill — enlarged for visibility */}
      <div
        style={{
          display:        'flex',
          alignItems:     'center',
          gap:            8,
          background:     `${col.ring}1a`,
          border:         `1.5px solid ${col.ring}70`,
          borderRadius:   12,
          padding:        '6px 14px',
          cursor:         'default',
          boxShadow:      urgency === 'critical' || urgency === 'expired' ? col.glow : 'none',
          animation:      urgency === 'critical' ? 'subPulse 2s ease infinite' : 'none',
        }}
      >
        {/* Clock icon */}
        <span style={{ fontSize: 18, lineHeight: 1 }}>🕐</span>

        {/* Live countdown label */}
        <span
          style={{
            fontSize:      16,
            fontWeight:    800,
            color:         col.ring,
            fontFamily:    'monospace',
            letterSpacing: '0.04em',
            whiteSpace:    'nowrap',
          }}
        >
          {countdown}
        </span>
      </div>

      {/* Tooltip */}
      {showTip && (
        <div
          style={{
            position:    'absolute',
            top:         '100%',
            right:       0,
            marginTop:   8,
            background:  '#0f172a',
            color:       '#e2e8f0',
            fontSize:    12,
            padding:     '9px 13px',
            borderRadius: 8,
            whiteSpace:  'nowrap',
            zIndex:      999,
            boxShadow:   '0 4px 20px rgba(0,0,0,.4)',
            border:      `1px solid ${col.ring}40`,
            lineHeight:  1.6,
          }}
        >
          <div style={{ fontWeight: 700, color: col.ring, marginBottom: 2 }}>
            Subscription Status
          </div>
          <div>{tooltipText}</div>
          {!isLifetime && !isExpired && (
            <div style={{ color: '#94a3b8', fontSize: 11, marginTop: 3, fontFamily: 'monospace' }}>
              {monthsRemaining > 0 && `${monthsRemaining}mo `}
              {daysRemaining}d {pad2(hoursRemaining)}:{pad2(minutesRemaining)}:{pad2(secondsRemaining)} remaining
            </div>
          )}
          {isExpired && (
            <div style={{ color: '#f87171', fontSize: 11, marginTop: 3 }}>
              Contact your administrator to renew.
            </div>
          )}
        </div>
      )}

      {/* Keyframe injected once */}
      <style>{`
        @keyframes subPulse {
          0%,100% { box-shadow: 0 0 4px ${col.ring}60; }
          50%      { box-shadow: 0 0 10px ${col.ring}; }
        }
      `}</style>
    </div>
  );
}

// ── UnreachableBanner — slim strip shown when Main Hall can't be reached ────

function UnreachableBanner({ onLogout }) {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;

  return (
    <div
      role="alert"
      aria-live="polite"
      style={{
        display:      'flex',
        alignItems:   'center',
        gap:          10,
        padding:      '0 16px 0 0',
        height:       38,
        flexShrink:   0,
        background:   '#FFFBEB',
        borderBottom: '1px solid #FDE68A',
        borderLeft:   '3px solid #F59E0B',
        fontFamily:   FONTS.body,
        overflow:     'hidden',
      }}
    >
      {/* Left accent already comes from borderLeft; icon gives extra clarity */}
      <span style={{
        display:     'flex',
        alignItems:  'center',
        justifyContent: 'center',
        width:       38,
        flexShrink:  0,
        fontSize:    14,
        color:       '#D97706',
      }}>
        ⚠
      </span>

      <p style={{ flex: 1, fontSize: 12, color: '#78350F', margin: 0, lineHeight: 1.3, minWidth: 0 }}>
        <strong style={{ color: '#B45309', fontWeight: 700 }}>
          Subscription service unreachable.
        </strong>
        {' '}Please{' '}
        <button
          onClick={onLogout}
          style={{
            background:    'none',
            border:        'none',
            padding:       0,
            margin:        0,
            fontSize:      12,
            fontWeight:    700,
            color:         '#B45309',
            cursor:        'pointer',
            textDecoration:'underline',
            fontFamily:    FONTS.body,
          }}
        >
          log out and log back in
        </button>
        {' '}to retry, or contact support if this persists.
      </p>

      {/* Log Out pill */}
      <button
        onClick={onLogout}
        style={{
          display:     'flex',
          alignItems:  'center',
          gap:         5,
          background:  '#FEF3C7',
          border:      '1px solid #FCD34D',
          borderRadius: 6,
          padding:     '4px 11px',
          fontSize:    11,
          fontWeight:  700,
          color:       '#92400E',
          cursor:      'pointer',
          fontFamily:  FONTS.body,
          flexShrink:  0,
          whiteSpace:  'nowrap',
          transition:  'background .15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = '#FDE68A'; }}
        onMouseLeave={e => { e.currentTarget.style.background = '#FEF3C7'; }}
      >
        🔑 Log Out
      </button>

      {/* Contact support */}
      <a
        href="mailto:support@casagrandehotel.com"
        style={{
          fontSize:       11,
          color:          '#D97706',
          textDecoration: 'none',
          flexShrink:     0,
          whiteSpace:     'nowrap',
          fontWeight:     600,
          padding:        '4px 0',
        }}
        onMouseEnter={e => { e.currentTarget.style.textDecoration = 'underline'; }}
        onMouseLeave={e => { e.currentTarget.style.textDecoration = 'none'; }}
      >
        Get Help →
      </a>

      {/* Dismiss */}
      <button
        onClick={() => setDismissed(true)}
        aria-label="Dismiss"
        style={{
          marginLeft:  4,
          background:  'none',
          border:      'none',
          cursor:      'pointer',
          fontSize:    14,
          color:       '#D97706',
          padding:     '0 6px',
          lineHeight:  1,
          flexShrink:  0,
          opacity:     0.6,
          transition:  'opacity .15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.opacity = '1'; }}
        onMouseLeave={e => { e.currentTarget.style.opacity = '0.6'; }}
      >
        ✕
      </button>
    </div>
  );
}

// ── SubscriptionWatchdog — warning banners + expired overlay ─────────────────

/**
 * Mount this ONCE at the root of FrontDeskLayout, outside the scrollable area.
 * It renders nothing visible for healthy subscriptions (urgency=ok).
 * On warning/critical it shows a dismissible banner.
 * On expiry it shows a full-screen lockout overlay and blocks the UI.
 */
export default function SubscriptionWatchdog({
  subscription,
  onLogout,
  tenantSlug,
  mainHallUrl,
}) {

  const slug      = tenantSlug ?? getTenantSlug();
  const mhUrl     = mainHallUrl
    ?? `${window.location.protocol}//${window.location.hostname}:3000`;
  const renewUrl  = `${mhUrl}/tenant/billing?tenant=${slug}`;

  // ── Block API requests + trigger logout when expired ───────────────────────
  useEffect(() => {
    if (!subscription || subscription.loading) return;

    const isInactive = subscription.isExpired || subscription.status === 'suspended';

    if (isInactive) {
      // Block all future fetch calls via the module flag
      setSubscriptionBlocked(true);

      // Dispatch global event so other parts of the app can react
      window.dispatchEvent(new CustomEvent('hotelmis:subscription:expired', {
        detail: { tenantSlug: slug, status: subscription.status },
      }));
    } else {
      // Subscription is active — unblock if previously blocked (e.g. renewed)
      setSubscriptionBlocked(false);
    }
  }, [subscription?.isExpired, subscription?.status, slug]);

  // No manual refresh — admin must activate the module in Control Centre

  // ── Nothing to show for healthy / loading subscriptions ───────────────────
  const isInactive = subscription?.isExpired || subscription?.status === 'suspended';
  if (!subscription || subscription.loading || (subscription.urgency === 'ok' && !isInactive)) {
    if (subscription?.unreachable) {
      return <UnreachableBanner onLogout={onLogout} />;
    }
    return null;
  }

  const { urgency, isExpired, isLifetime, daysRemaining, hoursRemaining,
          minutesRemaining, periodEnd, pricingModel, status, businessName,
          unreachable } = subscription;

  const col = URGENCY_COLORS[urgency] ?? URGENCY_COLORS.ok;

  // ── Inactive (expired or suspended): non-dismissible half-page modal ────────
  if (isInactive) {
    const isSuspended = status === 'suspended';
    return (
      <>
        {/* Backdrop — pointer events blocked, clicking does nothing */}
        <div
          style={{
            position:       'fixed',
            inset:          0,
            zIndex:         10_000,
            background:     'rgba(2, 6, 23, 0.75)',
            backdropFilter: 'blur(5px)',
            pointerEvents:  'all',
          }}
          /* Intentionally no onClick — modal is non-dismissible */
        />

        {/* Modal card — half-page width, centered */}
        <div
          role="alertdialog"
          aria-modal="true"
          aria-label="Module inactive"
          style={{
            position:        'fixed',
            top:             '50%',
            left:            '50%',
            transform:       'translate(-50%, -50%)',
            zIndex:          10_001,
            width:           'min(50vw, 640px)',
            minWidth:        320,
            background:      '#0f172a',
            border:          `1.5px solid ${isSuspended ? '#78350f' : '#1e293b'}`,
            borderRadius:    22,
            padding:         '52px 44px',
            textAlign:       'center',
            boxShadow:       '0 32px 96px rgba(0,0,0,.7)',
            fontFamily:      FONTS.body,
          }}
        >
          {/* Warning icon */}
          <div style={{ fontSize: 56, marginBottom: 22, lineHeight: 1 }}>
            {isSuspended ? '⚠️' : '🔒'}
          </div>

          {/* Title */}
          <h2 style={{
            fontSize:     24,
            fontWeight:   800,
            color:        '#f1f5f9',
            fontFamily:   FONTS.display ?? FONTS.body,
            marginBottom: 10,
            lineHeight:   1.2,
          }}>
            {isSuspended ? 'Module Suspended' : 'Subscription Inactive'}
          </h2>

          {/* Property name */}
          {businessName && (
            <p style={{ fontSize: 13, color: '#475569', marginBottom: 6 }}>
              {businessName}
            </p>
          )}

          {/* Status pill */}
          <div style={{
            display:      'inline-block',
            background:   isSuspended ? 'rgba(120,53,15,0.25)' : '#1e293b',
            border:       `1px solid ${isSuspended ? '#92400e' : '#334155'}`,
            borderRadius: 99,
            padding:      '3px 14px',
            fontFamily:   'monospace',
            fontSize:     11,
            color:        isSuspended ? '#fbbf24' : '#ef4444',
            marginBottom: 28,
            letterSpacing: '0.04em',
          }}>
            status: {status}
          </div>

          {/* Main message */}
          <div style={{
            background:   'rgba(255,255,255,0.04)',
            border:       '1px solid rgba(255,255,255,0.08)',
            borderRadius: 14,
            padding:      '22px 24px',
            marginBottom: 28,
          }}>
            <p style={{ fontSize: 15, color: '#cbd5e1', lineHeight: 1.75, margin: 0 }}>
              The <strong style={{ color: '#f1f5f9' }}>Reservation Module</strong> is
              currently <strong style={{ color: isSuspended ? '#fbbf24' : '#f87171' }}>
                {isSuspended ? 'suspended' : 'inactive'}
              </strong> and cannot be accessed.
            </p>
            <p style={{ fontSize: 15, color: '#cbd5e1', lineHeight: 1.75, margin: '12px 0 0' }}>
              Please ask your <strong style={{ color: '#f1f5f9' }}>administrator</strong>{' '}
              to activate this module from the{' '}
              <strong style={{ color: '#38bdf8' }}>Control Centre</strong>.
            </p>
          </div>

          {/* Footer note */}
          <p style={{ fontSize: 11, color: '#334155', lineHeight: 1.7, margin: 0 }}>
            All your data remains safe and will be accessible once the module is reactivated.
            <br />
            If you believe this is an error, contact your system administrator.
          </p>
        </div>
      </>
    );
  }

  // If Main Hall is unreachable right now, show that banner regardless of urgency
  if (unreachable) return <UnreachableBanner onLogout={onLogout} />;

  // ── Warning / critical: slim horizontal banner (cannot be dismissed) ──────

  const isCritical = urgency === 'critical';

  // Guard: if every time unit is zero and the sub isn't formally expired, the
  // data is degenerate (null periodEnd from a proxy error). Show unreachable.
  const allZero = !isLifetime && !isExpired
    && daysRemaining === 0 && hoursRemaining === 0 && minutesRemaining === 0;
  if (allZero) return <UnreachableBanner onLogout={onLogout} />;

  const timeLabel = daysRemaining >= 2
    ? `${daysRemaining} days`
    : daysRemaining === 1
      ? `${hoursRemaining} hours`
      : `${minutesRemaining} minutes`;

  // Slim, single-line banner that sits flush under the topnav
  const bannerBg     = isCritical ? '#FEF2F2' : '#FFFBEB';
  const bannerBorder = isCritical ? '#FECACA' : '#FDE68A';
  const bannerAccent = isCritical ? '#EF4444' : '#F59E0B';
  const bannerText   = isCritical ? '#7F1D1D' : '#78350F';
  const bannerStrong = isCritical ? '#B91C1C' : '#B45309';
  const pillBg       = isCritical ? '#FEE2E2' : '#FEF3C7';
  const pillBorder   = isCritical ? '#FCA5A5' : '#FCD34D';
  const pillText     = isCritical ? '#991B1B' : '#92400E';

  return (
    <div
      role="alert"
      style={{
        display:      'flex',
        alignItems:   'center',
        gap:          10,
        padding:      '0 16px 0 0',
        height:       38,
        flexShrink:   0,
        background:   bannerBg,
        borderBottom: `1px solid ${bannerBorder}`,
        borderLeft:   `3px solid ${bannerAccent}`,
        fontFamily:   FONTS.body,
        animation:    isCritical ? 'subBannerPulse 3s ease infinite' : 'none',
        overflow:     'hidden',
      }}
    >
      {/* Icon zone */}
      <span style={{
        display:        'flex',
        alignItems:     'center',
        justifyContent: 'center',
        width:          38,
        flexShrink:     0,
        fontSize:       13,
        color:          bannerAccent,
      }}>
        {isCritical ? '🚨' : '⚠'}
      </span>

      {/* Message */}
      <p style={{ flex: 1, fontSize: 12, color: bannerText, margin: 0, lineHeight: 1, minWidth: 0 }}>
        <strong style={{ color: bannerStrong, fontWeight: 700 }}>
          Subscription expiring in {timeLabel}
        </strong>
        {' — '}
        {isCritical
          ? 'Renew now to prevent an immediate service interruption.'
          : 'Renew soon to avoid losing access.'}
      </p>

      {/* Renew CTA pill */}
      <a
        href={renewUrl}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          display:        'flex',
          alignItems:     'center',
          gap:            4,
          background:     pillBg,
          border:         `1px solid ${pillBorder}`,
          borderRadius:   6,
          padding:        '4px 11px',
          fontSize:       11,
          fontWeight:     700,
          color:          pillText,
          textDecoration: 'none',
          flexShrink:     0,
          whiteSpace:     'nowrap',
          transition:     'background .15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.filter = 'brightness(0.95)'; }}
        onMouseLeave={e => { e.currentTarget.style.filter = 'none'; }}
      >
        🔑 Renew Now
      </a>

      <style>{`
        @keyframes subBannerPulse {
          0%,100% { opacity: 1; }
          50%      { opacity: 0.88; }
        }
      `}</style>
    </div>
  );
}